import type { StateCreator } from 'zustand'
import type { ScheduleGrid, ScheduleItem, ValidationResult, ItemStatus } from '../../types/index.js'
import { clampPpm, clampPpmw, PPM_DEFAULT, PPMW_DEFAULT } from '../../components/agenda/useAgendaLayout.js'
import { addBroadcastDays, broadcastDayKey, todayBroadcastDayKey } from '../../lib/broadcast-day.js'
import { getServerNow } from '../../hooks/useServerClock.js'

// ─── Vue agenda ───────────────────────────────────────────────────────────────

export type GridView = 'agenda' | 'playlist' | 'week'

export interface AgendaViewState {
  date:        string   // ISO date string (YYYY-MM-DD) — jour affiché
  view:        GridView
  pxPerMinute: number   // échelle de la vue Jour, 1 → 240 px/min (continue)
  pxPerMinuteWeek: number // échelle de la vue Semaine, 1 → 6 px/min
}

export interface AgendaFocusRequest {
  itemId:  string   // item à sélectionner une fois la grille chargée
  iso:     string   // instant à amener à l'écran (début du recouvrement)
  gridId:  string   // grille concernée (informatif ; la date rouvre la bonne)
  seq:     number   // incrémenté à chaque demande → déclencheur d'effet fiable
}

// ─── Slice ────────────────────────────────────────────────────────────────────

export interface ScheduleSlice {
  // State — grilles
  grids:           ScheduleGrid[]
  gridsLoading:    boolean
  gridsError:      string | null
  activeGridId:    string | null

  // State — items (pour le jour affiché)
  items:           ScheduleItem[]
  itemsLoading:    boolean
  conflicts:       string[]    // IDs des items en conflit après un move

  // State — items du jour d'antenne réel, indépendants de la navigation dans
  // la Grille (cf. useLiveSchedule) — alimentent le bandeau permanent.
  liveItems:        ScheduleItem[]
  liveItemsLoading: boolean
  /**
   * Queue de nuit de la journée affichée (`lib/night-tail.ts`), rangée sous la
   * clé `grille|jour` qui l'a lue : une autre journée ne la voit jamais.
   */
  agendaNightTail: { key: string; endMs: number; nextItem: ScheduleItem | null } | null

  // State — validation
  validationResult: ValidationResult | null
  validating:       boolean

  // State — vue agenda
  agendaView:      AgendaViewState

  // State — demande de focus agenda : amener la timeline sur un item et le
  // sélectionner, sans le modifier (cf. bouton « Localiser » d'un chevauchement).
  agendaFocus:     AgendaFocusRequest | null

  // State — drag & drop
  draggingItemId:  string | null

  // State — séquence de mise à jour grille (incrémenté à chaque GRID_UPDATED WS)
  gridUpdateSeq:   number

  // State — séquence de mise à jour liste de grilles (incrémenté à chaque GRIDS_UPDATED WS)
  gridsUpdateSeq:  number

  // Actions — grilles
  setGrids:        (grids: ScheduleGrid[]) => void
  setActiveGrid:   (id: string | null) => void
  setGridsLoading: (loading: boolean) => void
  setGridsError:   (err: string | null) => void
  upsertGrid:      (g: ScheduleGrid) => void
  removeGrid:      (id: string) => void

  // Actions — items
  setItems:           (items: ScheduleItem[]) => void
  setItemsLoading:    (loading: boolean) => void
  upsertItem:         (item: ScheduleItem) => void
  removeItem:         (id: string) => void
  updateItemStatus:   (id: string, status: ItemStatus) => void
  setConflicts:       (ids: string[]) => void
  clearConflicts:     () => void

  // Actions — items du jour d'antenne réel
  setLiveItems:        (items: ScheduleItem[]) => void
  setLiveItemsLoading: (loading: boolean) => void
  setAgendaNightTail:  (tail: ScheduleSlice['agendaNightTail']) => void

  // Actions — validation
  setValidationResult: (r: ValidationResult | null) => void
  setValidating:       (v: boolean) => void

  // Actions — vue agenda
  setAgendaDate:   (date: string) => void
  setAgendaView:   (view: GridView) => void
  setPxPerMinute:  (ppm: number) => void
  setPxPerMinuteWeek: (ppm: number) => void
  goToToday:       () => void
  shiftDay:        (delta: number) => void
  focusAgendaItem: (p: { itemId: string; iso: string; gridId: string }) => void

  // Actions — drag & drop
  setDraggingItem: (id: string | null) => void

  // Actions — reload grille
  incrementGridUpdateSeq:  () => void
  incrementGridsUpdateSeq: () => void

  // Sélecteurs
  activeGrid:      () => ScheduleGrid | null
  onAirItem:       () => ScheduleItem | null
  nextItem:        () => ScheduleItem | null
  itemsByHour:     (hour: number) => ScheduleItem[]
}

// ─── Helper date ──────────────────────────────────────────────────────────────

/**
 * Le jour ouvert par défaut est celui de l'antenne, pas celui du poste : à
 * 00:30 heure de Paris, l'opérateur doit voir la journée qui vient de commencer,
 * y compris si sa machine est restée sur la veille.
 *
 * L'instant vient du serveur (`useServerClock`), jamais de `new Date()` : c'est
 * l'horloge du serveur qui déclenche les départs, et un poste en avance de deux
 * minutes basculerait sur le lendemain avant l'antenne — exactement le décalage
 * qu'on cherche à supprimer au passage d'une journée à l'autre.
 */
function todayISO(): string {
  return todayBroadcastDayKey(getServerNow())
}

/** Jour suivant ou précédent, en jours civils d'antenne. */
function shiftDateISO(dateISO: string, days: number): string {
  return broadcastDayKey(addBroadcastDays(dateISO, days))
}

// ─── Créateur ─────────────────────────────────────────────────────────────────

export const createScheduleSlice: StateCreator<ScheduleSlice, [], [], ScheduleSlice> = (set, get) => ({
  // ── État initial ─────────────────────────────────────────────────────────
  grids:             [],
  gridsLoading:      false,
  gridsError:        null,
  activeGridId:      null,
  items:             [],
  itemsLoading:      false,
  liveItems:         [],
  liveItemsLoading:  false,
  agendaNightTail:   null,
  conflicts:         [],
  validationResult:  null,
  validating:        false,
  gridUpdateSeq:     0,
  gridsUpdateSeq:    0,
  agendaView: {
    date:        todayISO(),
    view:        'agenda',
    pxPerMinute: PPM_DEFAULT,
    pxPerMinuteWeek: PPMW_DEFAULT,
  },
  agendaFocus:       null,
  draggingItemId:    null,

  // ── Grilles ──────────────────────────────────────────────────────────────
  setGrids:        (grids)   => set({ grids }),
  setActiveGrid:   (id)      => set({ activeGridId: id }),
  setGridsLoading: (loading) => set({ gridsLoading: loading }),
  setGridsError:   (err)     => set({ gridsError: err }),

  upsertGrid: (g) =>
    set(state => {
      const idx = state.grids.findIndex(x => x.id === g.id)
      return {
        grids: idx >= 0
          ? state.grids.map(x => x.id === g.id ? g : x)
          : [g, ...state.grids],
      }
    }),

  removeGrid: (id) =>
    set(state => ({
      grids:       state.grids.filter(g => g.id !== id),
      activeGridId: state.activeGridId === id ? null : state.activeGridId,
    })),

  // ── Items ─────────────────────────────────────────────────────────────────
  setItems:        (items)   => set({ items }),
  setItemsLoading: (loading) => set({ itemsLoading: loading }),

  upsertItem: (item) =>
    set(state => {
      const idx = state.items.findIndex(x => x.id === item.id)
      const items = idx >= 0
        ? state.items.map(x => x.id === item.id ? item : x)
        : [...state.items, item]
      // Re-trie par startTime après un upsert
      items.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())

      // Même mise à jour côté items « jour d'antenne réel », uniquement si
      // l'item y figure déjà — sinon il appartient à un autre jour consulté.
      const liveIdx = state.liveItems.findIndex(x => x.id === item.id)
      const liveItems = liveIdx >= 0
        ? state.liveItems.map(x => x.id === item.id ? item : x)
        : state.liveItems

      return { items, liveItems }
    }),

  removeItem: (id) =>
    set(state => ({
      items:     state.items.filter(i => i.id !== id),
      liveItems: state.liveItems.filter(i => i.id !== id),
    })),

  updateItemStatus: (id, status) =>
    set(state => ({
      items:     state.items.map(i => i.id === id ? { ...i, status } : i),
      liveItems: state.liveItems.map(i => i.id === id ? { ...i, status } : i),
    })),

  setConflicts:  (ids) => set({ conflicts: ids }),
  clearConflicts: ()   => set({ conflicts: [] }),

  // ── Items du jour d'antenne réel ─────────────────────────────────────────
  setLiveItems:        (items)   => set({ liveItems: items }),
  setLiveItemsLoading: (loading) => set({ liveItemsLoading: loading }),
  setAgendaNightTail:  (tail) => set({ agendaNightTail: tail }),

  // ── Validation ────────────────────────────────────────────────────────────
  setValidationResult: (r) => set({ validationResult: r }),
  setValidating:       (v) => set({ validating: v }),

  // ── Vue agenda ────────────────────────────────────────────────────────────
  setAgendaDate: (date) =>
    set(state => ({ agendaView: { ...state.agendaView, date } })),

  setAgendaView: (view) =>
    set(state => ({ agendaView: { ...state.agendaView, view } })),

  setPxPerMinute: (ppm: number) =>
    set(state => ({ agendaView: { ...state.agendaView, pxPerMinute: clampPpm(ppm) } })),

  setPxPerMinuteWeek: (ppm: number) =>
    set(state => ({ agendaView: { ...state.agendaView, pxPerMinuteWeek: clampPpmw(ppm) } })),

  goToToday: () =>
    set(state => ({ agendaView: { ...state.agendaView, date: todayISO() } })),

  shiftDay: (delta) =>
    set(state => ({
      agendaView: {
        ...state.agendaView,
        date: shiftDateISO(state.agendaView.date, delta),
      },
    })),

  focusAgendaItem: ({ itemId, iso, gridId }) =>
    set(state => ({
      agendaFocus: { itemId, iso, gridId, seq: (state.agendaFocus?.seq ?? 0) + 1 },
    })),

  // ── Drag & drop ───────────────────────────────────────────────────────────
  setDraggingItem: (id) => set({ draggingItemId: id }),

  // ── Reload grille ─────────────────────────────────────────────────────────
  incrementGridUpdateSeq:  () => set(state => ({ gridUpdateSeq:  state.gridUpdateSeq  + 1 })),
  incrementGridsUpdateSeq: () => set(state => ({ gridsUpdateSeq: state.gridsUpdateSeq + 1 })),

  // ── Sélecteurs ────────────────────────────────────────────────────────────
  activeGrid: () => {
    const { grids, activeGridId } = get()
    return grids.find(g => g.id === activeGridId) ?? null
  },

  onAirItem: () =>
    get().items.find(i => i.status === 'ON_AIR') ?? null,

  nextItem: () =>
    get().items.find(i => i.status === 'SCHEDULED') ?? null,

  itemsByHour: (hour: number) => {
    return get().items.filter(item => {
      const start = new Date(item.startTime)
      return start.getHours() === hour
    })
  },
})
